import { Link } from 'react-router-dom';
import { Check } from 'lucide-react';
import { usePageMeta, Reveal, Count } from '../lib/site';
import { CardHeader, CtaBand } from '../components/Bits';

const INCLUDED = [
  'Tee sheet',
  'Rate engine, rack and app rate',
  'Dynamic pricing with your floor and ceiling',
  'Flash Deals',
  'In-house POS with split bill and pay-from-phone',
  'Staff scheduling',
  'Issue and maintenance tracking',
  'Supplier ordering with catalogs and stock visibility',
  'Your course listed and bookable in the player app',
];

const FAQS = [
  {
    q: 'We already run tee sheet software. What does switching look like?',
    a: 'LinksOS is tee sheet software, so implementing it means it becomes your tee sheet; it does not stack on top of Lightspeed or Teesnap. We do the setup with you, and the demo is where we map out what moving your operation over looks like. The reason courses make the switch is what comes attached: the MyGolfLinks app that fills the sheet.',
  },
  {
    q: 'Who owns the golfer relationship?',
    a: 'You do. Bookings land with the golfer’s name and contact attached, in your book, and we never resell your inventory. The only booking fee anywhere is a flat $1.80 when a golfer books through the app, shown upfront before they pay. Booking through your own website carries no MyGolfLinks fee.',
  },
  {
    q: 'What happens when our volume grows?',
    a: 'The plan price stays flat. On LinksOS, the 0.5% per-round commission grows with your volume by design; when it crosses the price gap, moving to LinksOS+ ends it. The $1.80 app booking fee is paid by the golfer either way.',
  },
  {
    q: 'When do the Flash Deal and POS fees get published?',
    a: 'They are finalized at launch. They are per-transaction, tied to sales that actually happen, and you will see them in writing before you are ever charged one.',
  },
];

export default function Pricing() {
  usePageMeta(
    'Pricing | MyGolfLinks',
    'LinksOS at $197 a month and LinksOS+ at $495, everything included. A flat $1.80 booking fee on app bookings. No barter, no parity clauses.'
  );

  return (
    <main>
      <section style={{ padding: 'clamp(140px, 16vw, 180px) 0 clamp(40px, 5vw, 60px)' }}>
        <div className="wrap">
          <Reveal>
            <p className="mono" style={{ fontSize: 12.5, letterSpacing: '0.14em', textTransform: 'uppercase', color: 'var(--fairway)', fontWeight: 500, marginBottom: 20 }}>
              Pricing
            </p>
            <h1 className="display" style={{ maxWidth: 780 }}>Everything included. Priced in the open.</h1>
            <p className="lede" style={{ margin: '24px 0 0' }}>
              Two plan sizes, both with the full platform. Small transaction fees on top, only when
              money moves. This page is the whole price list.
            </p>
          </Reveal>
        </div>
      </section>

      {/* ── Rate boards ── */}
      <section>
        <div className="wrap" style={{ padding: '0 clamp(20px, 4.5vw, 44px) clamp(56px, 7vw, 80px)' }}>
          <Reveal className="g2-tight" style={{ maxWidth: 780 }}>
            <div className="rb">
              <div className="mono" style={{ fontSize: 11, letterSpacing: '0.12em', textTransform: 'uppercase', color: 'var(--mut)', marginBottom: 14 }}>
                LinksOS
              </div>
              <div className="rb-price">$197</div>
              <div className="rb-per">a month</div>
              <ul>
                <li><Check size={15} aria-hidden="true" />For smaller courses, under about 24,000 rounds a year</li>
                <li><Check size={15} aria-hidden="true" />The full platform, everything on this page</li>
                <li><Check size={15} aria-hidden="true" />Plus 0.5% of green-fee revenue per round</li>
              </ul>
            </div>
            <div className="rb">
              <div className="mono" style={{ fontSize: 11, letterSpacing: '0.12em', textTransform: 'uppercase', color: 'var(--mut)', marginBottom: 14 }}>
                LinksOS+
              </div>
              <div className="rb-price">$495</div>
              <div className="rb-per">a month</div>
              <ul>
                <li><Check size={15} aria-hidden="true" />For mid-size and large courses, about 24,000 rounds a year and up</li>
                <li><Check size={15} aria-hidden="true" />The full platform, everything on this page</li>
                <li><Check size={15} aria-hidden="true" />No per-round commission</li>
              </ul>
            </div>
          </Reveal>
          <Reveal d={1} style={{ maxWidth: 780, marginTop: 18, display: 'grid', gap: 12 }}>
            <p style={{ fontSize: '0.95rem', color: 'var(--mut)' }}>
              The split is deliberate. On LinksOS, the small per-round commission grows with your
              rounds, and once it crosses the price difference, LinksOS+ is simply the cheaper
              plan. Do that math with us on the demo; we will tell you straight which side of the
              line your course sits on.
            </p>
            <div
              className="on-pine"
              style={{
                background: 'var(--pine-deep)', color: 'var(--cream)', borderRadius: 14,
                padding: '24px 28px', display: 'flex', gap: 24, alignItems: 'center', flexWrap: 'wrap',
              }}
            >
              <div>
                <span className="mono" style={{ fontSize: '2.2rem', fontWeight: 600, color: 'var(--gold-bright)' }}>$400</span>
                <span className="mono" style={{ fontSize: '0.85rem', letterSpacing: '0.12em', textTransform: 'uppercase', color: 'var(--cream-mut)', marginLeft: 10 }}>once</span>
              </div>
              <p style={{ fontSize: '0.95rem', color: 'var(--cream-mut)', flex: 1, minWidth: 240 }}>
                Right now we are in the founding window: founding courses pay $400 one time instead
                of the plans on this page, and keep the platform forever. Transaction fees are the
                same for everyone. <Link to="/founding" className="tlink">The founding deal</Link>
              </p>
            </div>
          </Reveal>
        </div>
      </section>

      {/* ── Included, not an add-on ── */}
      <section className="band-pine on-pine">
        <div className="wrap" style={{ padding: 'clamp(64px, 8vw, 100px) clamp(20px, 4.5vw, 44px)' }}>
          <Reveal>
            <CardHeader no="01" label="The wedge" dark />
            <h2 className="h2" style={{ maxWidth: 660 }}>Included, not an add-on</h2>
            <p className="lede" style={{ marginTop: 16 }}>
              Elsewhere, the list below is how the invoice grows: a module here, a per-feature fee
              there. Here it is just the plan. No asterisks anywhere on this page.
            </p>
          </Reveal>
          <Reveal d={1} style={{ marginTop: 36, display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '4px 40px' }}>
            {INCLUDED.map((item) => (
              <div key={item} style={{ display: 'flex', gap: 12, alignItems: 'baseline', padding: '12px 0', borderBottom: '1px solid var(--cream-line)', fontSize: '1rem' }}>
                <Check size={16} aria-hidden="true" style={{ color: 'var(--gold-bright)', flexShrink: 0, position: 'relative', top: 2 }} />
                {item}
              </div>
            ))}
          </Reveal>
        </div>
      </section>

      {/* ── Transaction fees ── */}
      <section>
        <div className="wrap" style={{ padding: 'clamp(64px, 8vw, 100px) clamp(20px, 4.5vw, 44px)' }}>
          <Reveal>
            <CardHeader no="02" label="Transaction fees" />
            <h2 className="h2" style={{ maxWidth: 640 }}>We earn when you earn. That's the whole model.</h2>
          </Reveal>
          <Reveal className="ledger" style={{ marginTop: 38, maxWidth: 820 }} d={1}>
            <div className="led" style={{ gridTemplateColumns: '190px 1fr' }}>
              <span className="mono" style={{ fontSize: '1.4rem', fontWeight: 600 }}>
                $<Count value={1.8} decimals={2} />
              </span>
              <span className="led-body">
                flat booking fee per player booked through the app, shown upfront at checkout.
                Bookings through your own website carry no MyGolfLinks fee.
              </span>
            </div>
            <div className="led" style={{ gridTemplateColumns: '190px 1fr' }}>
              <span className="mono" style={{ fontSize: '1.4rem', fontWeight: 600 }}>
                $<Count value={0} />
              </span>
              <span className="led-body">
                to you on supplier orders through the platform. The supplier pays the platform fee;
                your invoice does not.
              </span>
            </div>
            <div className="led" style={{ gridTemplateColumns: '190px 1fr' }}>
              <span className="mono" style={{ fontSize: '1.4rem', fontWeight: 600 }}>at launch</span>
              <span className="led-body">
                per-transaction fees on Flash Deals and in-house POS sales are finalized and put in
                writing before you are ever charged one.
              </span>
            </div>
          </Reveal>
          <Reveal d={2} style={{ marginTop: 30, display: 'flex', gap: 10, flexWrap: 'wrap' }}>
            {['No barter', 'No parity clauses', 'No add-on modules', 'No fees on your own website'].map((t) => (
              <span
                key={t}
                className="mono"
                style={{
                  fontSize: 12.5, fontWeight: 500, color: 'var(--pine)',
                  border: '1px solid var(--line)', background: 'var(--card)',
                  borderRadius: 20, padding: '8px 14px',
                }}
              >
                {t}
              </span>
            ))}
          </Reveal>
        </div>
      </section>

      {/* ── Questions ── */}
      <section className="band-card">
        <div className="wrap" style={{ padding: 'clamp(64px, 8vw, 100px) clamp(20px, 4.5vw, 44px)' }}>
          <Reveal>
            <CardHeader no="03" label="Fair questions" />
            <h2 className="h2" style={{ maxWidth: 620 }}>Asked by every GM, answered straight</h2>
          </Reveal>
          <Reveal d={1} style={{ marginTop: 36, display: 'grid', gap: 12, maxWidth: 820 }}>
            {FAQS.map((f) => (
              <details className="how" key={f.q}>
                <summary>{f.q}</summary>
                <div className="how-body">
                  <p style={{ fontSize: '0.95rem', color: 'var(--mut)' }}>{f.a}</p>
                </div>
              </details>
            ))}
          </Reveal>
        </div>
      </section>

      <CtaBand
        title="Get the number for your course"
        sub="Bring your rounds and your green fee. We'll put them in the calculator on the call and let the arithmetic do the selling."
      />
    </main>
  );
}
